import type {
  ApplyApiKeyModelNamespaceResponse,
  ApiKeyModelMappingConflict,
  ListApiKeyModelMappingsResponse,
  SetApiKeyAutoModelNamespaceResponse,
  UpsertApiKeyModelMappingRequest,
  UpsertApiKeyModelMappingResponse,
} from '@nadoutong/contracts';
import { PlatformApiError, platformApiRequest } from './client.ts';

/**
 * Typed wrappers around platform-api `/v1/keys/:id/model-mappings*`
 * (epic #898 P3 #903, consumed by the tenant console since P6 #913).
 *
 * A mapping is `alias → sku_id`: the alias is the bare model name a
 * client sends in `model`, and the SKU fixes the marketplace listing it
 * resolves to. Aliases never contain `/` — platform-api rejects them.
 *
 * Every function accepts a `fetchImpl` so tests can inject a fake,
 * matching the `keys.ts` sibling.
 */

const BASE_PATH = '/v1/keys';

/** A typed mapping conflict with the server's candidate/source evidence intact. */
export class ModelMappingConflictError extends PlatformApiError {
  readonly conflicts: readonly ApiKeyModelMappingConflict[];

  constructor(cause: PlatformApiError, conflicts: readonly ApiKeyModelMappingConflict[]) {
    super(
      cause.status,
      cause.code,
      cause.message,
      cause.retryable,
      cause.responseBody,
    );
    this.name = 'ModelMappingConflictError';
    this.conflicts = conflicts;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isMappingConflict(value: unknown): value is ApiKeyModelMappingConflict {
  if (!isRecord(value) || typeof value.alias !== 'string') return false;
  if (value.reason !== 'multiple_targets' && value.reason !== 'invalid_alias') return false;
  if (!Array.isArray(value.candidates)) return false;
  return value.candidates.every((candidate) => {
    if (!isRecord(candidate)) return false;
    if (
      typeof candidate.sku_id !== 'string'
      || typeof candidate.sku_name !== 'string'
      || typeof candidate.namespace_id !== 'string'
      || !Array.isArray(candidate.sources)
    ) {
      return false;
    }
    return candidate.sources.every((source) => source === 'manual' || source === 'automatic');
  });
}

function conflictsFromError(error: PlatformApiError): readonly ApiKeyModelMappingConflict[] | null {
  if (error.code !== 'MODEL_MAPPING_CONFLICT' || !isRecord(error.responseBody)) return null;
  const conflicts = error.responseBody.conflicts;
  return Array.isArray(conflicts) && conflicts.every(isMappingConflict) ? conflicts : null;
}

async function modelMappingRequest<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (error) {
    if (error instanceof PlatformApiError) {
      const conflicts = conflictsFromError(error);
      if (conflicts !== null) throw new ModelMappingConflictError(error, conflicts);
    }
    throw error;
  }
}

function mappingPath(keyId: string, alias?: string): string {
  const base = `${BASE_PATH}/${encodeURIComponent(keyId)}/model-mappings`;
  return alias === undefined ? base : `${base}/${encodeURIComponent(alias)}`;
}

function namespaceMappingPath(
  keyId: string,
  namespaceId: string,
  operation: 'apply' | 'auto',
): string {
  return `${mappingPath(keyId)}/namespaces/${encodeURIComponent(namespaceId)}/${operation}`;
}

export async function listKeyModelMappings(
  keyId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListApiKeyModelMappingsResponse> {
  return platformApiRequest<ListApiKeyModelMappingsResponse>(
    mappingPath(keyId),
    { signal },
    fetchImpl,
  );
}

export async function upsertKeyModelMapping(
  keyId: string,
  alias: string,
  body: UpsertApiKeyModelMappingRequest,
  fetchImpl: typeof fetch,
): Promise<UpsertApiKeyModelMappingResponse> {
  return modelMappingRequest(() =>
    platformApiRequest<UpsertApiKeyModelMappingResponse>(
      mappingPath(keyId, alias),
      { method: 'PUT', body },
      fetchImpl,
    ),
  );
}

export async function deleteKeyModelMapping(
  keyId: string,
  alias: string,
  fetchImpl: typeof fetch,
): Promise<null> {
  return platformApiRequest<null>(
    mappingPath(keyId, alias),
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function applyKeyModelNamespace(
  keyId: string,
  namespaceId: string,
  fetchImpl: typeof fetch,
): Promise<ApplyApiKeyModelNamespaceResponse> {
  return modelMappingRequest(() =>
    platformApiRequest<ApplyApiKeyModelNamespaceResponse>(
      namespaceMappingPath(keyId, namespaceId, 'apply'),
      { method: 'POST' },
      fetchImpl,
    ),
  );
}

export async function enableKeyAutoModelNamespace(
  keyId: string,
  namespaceId: string,
  fetchImpl: typeof fetch,
): Promise<SetApiKeyAutoModelNamespaceResponse> {
  return modelMappingRequest(() =>
    platformApiRequest<SetApiKeyAutoModelNamespaceResponse>(
      namespaceMappingPath(keyId, namespaceId, 'auto'),
      { method: 'PUT' },
      fetchImpl,
    ),
  );
}

export async function disableKeyAutoModelNamespace(
  keyId: string,
  namespaceId: string,
  fetchImpl: typeof fetch,
): Promise<null> {
  return platformApiRequest<null>(
    namespaceMappingPath(keyId, namespaceId, 'auto'),
    { method: 'DELETE' },
    fetchImpl,
  );
}
